import { UseQueryOptions, UseQueryResult, useQuery } from '@tanstack/react-query'
import { featureFlagApi, FeatureResult, getPublicFeatureFlag } from '~/api/featureFlag'
import { forceArray } from '~/common/arrayUtils'
import { useQueryWithLocalStoragePersist } from '~/common/react-query/useQueryWithLocalStoragePersist'
import * as allFeatureFlags from '~/settings/vndly_config/feature_flags/feature_flags'

export const useFeatureFlag = <T extends string | string[] | readonly string[]>(
  features: T,
  opts?: UseQueryOptions<any>
): UseQueryResult<FeatureResult<T>> => {
  const queryKey = ['featureFlag', forceArray(features).sort()] as const
  return useQuery({
    queryKey,
    queryFn: () => featureFlagApi.get(features),
    staleTime: Infinity,
    cacheTime: Infinity,
    enabled: features.length > 0,
    ...opts
  })
}

export const useFeatureFlagLocalStorage = <T extends string | string[] | readonly string[]>(
  features: T,
  opts?: UseQueryOptions<any>
) => {
  const queryKey = ['featureFlag', forceArray(features).sort()] as const
  return useQueryWithLocalStoragePersist(queryKey, () => featureFlagApi.get(features), {
    staleTime: Infinity,
    cacheTime: Infinity,
    enabled: features.length > 0,
    localStorageKey: 'feature_flag/' + forceArray(features).sort().toString(),
    preventClientServerMismatches: true,
    ...opts
  })
}

/** Allow-listed flags only (`customers.api`); delegates to `useFeatureFlagLocalStorage` with the public URL. */
export const usePublicFeatureFlagLocalStorage = (
  flag: Parameters<typeof getPublicFeatureFlag>[0],
  opts?: UseQueryOptions<boolean>
) =>
  useFeatureFlagLocalStorage(flag, {
    ...opts,
    queryFn: () => getPublicFeatureFlag(flag)
  })

// This shouldn't be used anymore
// Use assets/js/settings/vndly_config/feature_flags/feature_flags.ts instead.
// TODO: update components using constants from this file
export const featureFlags = {
  ...allFeatureFlags,
  SHOW_US_MAPS: 'show_us_maps_in_dashboard',
  SHOW_WORLD_MAPS: 'show_world_maps_in_dashboard',
  HOLIDAY_START_END_TIME: 'holiday_start_end_time'
}

Object.freeze(featureFlags)
